import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';

// Program name inside the Learning Framework name pill — used on both the
// admin (ProgramCard) and learner (MemberProgramCard) cards.
//
// The pill grows with the name up to NAME_MAX_WIDTH, which fits the longest
// name we want shown in full ("New Business Development Excellence"); beyond
// that the name truncates on one line and shows the full title on hover —
// but only when it is actually cut off, so short names don't carry a
// pointless tooltip.
//
// A locked program greys its name rather than carrying a badge in the pill;
// the padlock sits in the card's top-right corner.
const NAME_MAX_WIDTH = 300;

// `compact` drops the name to 13px — the size the admin framework cards use
// since the split dashboard narrowed the columns, matching the gap titles in
// the Gaps & Risks panel beside them. The learner card keeps the 15px default.
const ProgramNameText: React.FC<{ name: string; locked?: boolean; compact?: boolean }> = ({
  name,
  locked = false,
  compact = false
}) => {
  const ref = useRef<HTMLElement | null>(null);
  const [truncated, setTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  // Re-measure on mount, on name change, and whenever the card is resized
  // (the grid reflows the cards at different breakpoints).
  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, name]);

  return (
    <Tooltip title={truncated ? name : ''} placement="top">
      <Typography
        ref={ref}
        sx={{
          color: locked ? '#868686' : '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: compact ? 13 : 15,
          fontWeight: 600,
          lineHeight: 1.2,
          minWidth: 0,
          maxWidth: NAME_MAX_WIDTH,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {name}
      </Typography>
    </Tooltip>
  );
};

export default ProgramNameText;
